<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames } from '@components/utils';
import { CdsCardGroupSymbol, useInjectCard } from '@components/components/CdsCard/context';

import {
  makeDimensionProps,
  makeDisabledProps,
  makeGroupItemProps,
  makeIconProps,
  makeLinkProps,
  makeLoadingProps,
  makeTagProps,
  makeTextProps,
  makeThemeProps,
  makeTitleProps,
  provideTheme,
  useDimensions,
  useGroupItem,
  useLink,
  useLoading,
  useRender
} from '../../composable';
import { CdsImage } from '../CdsImage';
import { CdsProgress } from '../CdsProgress';

import CdsCardActions from './CdsCardActions.vue';
import CdsCardItem from './CdsCardItem.vue';
import CdsCardText from './CdsCardText.vue';
import CdsCardDivider from './CdsCardDivider.vue';

import type { PropType } from 'vue';

export const componentName = 'CdsCard';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Весь компонент кликабельный. Так же применяется, если есть атрибуты href или to
     */
    interactive: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Фоновое изображение
     */
    image: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Подзаголовок
     */
    subtitle: {
      type: [String] as PropType<string>,
      default: ''
    },
    /**
     * Отобразить разделитель между заголовком и текстом
     */
    divider: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Состояние выбранное карточки
     */
    selected: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Без рамки (только контент)
     */
    borderless: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeTitleProps(),
    ...makeTextProps(),
    ...makeIconProps(),
    ...makeLoadingProps(),
    ...makeDimensionProps(),
    ...makeTagProps(),
    ...makeLinkProps(),
    ...makeGroupItemProps(),
    ...makeDisabledProps(),
    ...makeThemeProps()
  },
  emits: {
    click: (event: MouseEvent) => true,
    'group:selected': (value: { value: boolean }) => true
  },
  setup(props, { slots, emit }) {
    const cardGroup = useInjectCard();
    const groupItem = useGroupItem(props, CdsCardGroupSymbol, false);
    const { themeClasses } = provideTheme(props);
    const { loadingClasses } = useLoading(props);
    const { dimensionsStyles } = useDimensions(props);

    const { isLink, tag, href, to } = useLink(props, emit);

    const isDisabled = computed(() => props.disabled);
    const isSelected = computed(() => unref(groupItem?.isSelected) || props.selected);
    const isMultiple = computed(() => unref(cardGroup?.multiple));
    const isBorderless = computed(() => props.borderless);

    const isSelectable = computed(() => !!unref(groupItem?.value));
    const isClickable = computed(
      () =>
        (!props.disabled && props.interactive !== false && props.interactive) || unref(isLink) || unref(isSelectable)
    );

    const handleClick = (event: MouseEvent) => {
      if (!unref(isClickable)) return;

      if (groupItem) {
        groupItem.select(!unref(groupItem.isSelected));
      }

      emit('click', event);
    };

    useRender(() => {
      const hasTitle = !!slots.title || !!props.title;
      const hasSubtitle = !!slots.subtitle || !!props.subtitle;
      const hasAppend = !!slots.append || !!props.appendIcon;
      const hasPrepend = !!slots.prepend || !!props.prependIcon;
      const hasText = !!slots.text || !!props.text;
      const hasImage = !!slots.image || !!props.image;
      const hasHeader = hasTitle || hasSubtitle;
      const hasCardItem = hasHeader || hasAppend || hasPrepend;
      const hasDivider = hasText && hasCardItem && props.divider;

      return h(
        unref(isLink) ? unref(tag) : props.tag,
        {
          href: unref(href),
          to: unref(to),
          class: classNames(
            'cds-card',
            {
              'cds-card--disabled': unref(isDisabled),
              'cds-card--selected': unref(isSelected),
              'cds-card--interactive': unref(isClickable),
              'cds-card--multiple': unref(isMultiple),
              'cds-card--borderless': unref(isBorderless)
            },
            unref(themeClasses),
            unref(loadingClasses)
          ),
          style: {
            ...unref(dimensionsStyles)
          },
          onClick: handleClick,
          tabindex: unref(isClickable) ? 0 : -1
        },
        [
          hasImage &&
            h(
              'div',
              { key: 'image', class: 'cds-card__image' },
              !slots.image ? h(CdsImage, { key: 'image-img', cover: true, src: props.image }) : slots.image()
            ),

          h(
            'div',
            { class: 'cds-card__loader' },
            h(CdsProgress, {
              active: !!props.loading,
              height: 4,
              absolute: true,
              indeterminate: true
            })
          ),

          hasCardItem &&
            h(
              CdsCardItem,
              {
                key: 'item',
                title: props.title,
                subtitle: props.subtitle,
                prependIcon: props.prependIcon,
                appendIcon: props.appendIcon
              },
              {
                default: slots.item,
                title: slots.title,
                subtitle: slots.subtitle,
                prepend: slots.prepend,
                append: slots.append
              }
            ),

          hasDivider && h(CdsCardDivider),

          hasText &&
            h(
              CdsCardText,
              { key: 'text' },
              {
                default: () => slots.text?.() ?? props.text
              }
            ),

          slots.default?.(),

          slots.actions?.() &&
            h(
              CdsCardActions,
              { key: 'actions' },
              {
                default: () => slots.actions?.()
              }
            )
        ]
      );
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsCard';
</style>
